import { HeartHandshake, Globe2, BookOpen, Users, CheckCircle2, ArrowRight } from "lucide-react"
import { Button } from "../components/ui/button"

export default function EducationPaix() {
    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            {/* Hero Section - Standard (ref: DroitEcoGestion) */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Engagement Sociétal</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Vol. 2024</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Construire la</span>
                            <span className="block text-white mt-2">Paix Durable</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Fidèle à sa devise "Paix, Savoir, Développement", l'UNG place l'éducation à la paix au cœur de son projet pédagogique.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            {/* <Button className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Rejoindre un Club
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                Nos Programmes
                            </Button> */}
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats Bar */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "100%", label: "Cours Transversaux" },
                        { val: "5+", label: "Clubs de Paix" },
                        { val: "Anuel", label: "Conférences Int." },
                        { val: "UNG", label: "Pilier Social" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Sommaire
                            </p>
                            {["Vision", "Actions", "Impact", "Engagement"].map((item, i) => (
                                <a key={i} href={`#${item.toLowerCase()}`} className="group flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground hover:text-primary transition-smooth">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item}
                                </a>
                            ))}
                        </nav>
                        <div className="mt-12 pt-8 border-t border-border">
                            <div className="bg-primary/5 p-6 border-l-4 border-primary">
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-4 text-primary">Le Saviez-vous ?</h4>
                                <p className="text-[11px] text-muted-foreground leading-relaxed">
                                    L'UNG a été fondée dans un contexte de reconstruction, faisant de la paix sa mission première.
                                </p>
                            </div>
                        </div>
                    </aside>

                    {/* Body */}
                    <main className="space-y-16">
                        {/* Section 01 */}
                        <section id="vision">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Un Pilier Fondamental
                            </h2>
                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    Dans une région qui a connu des conflits, l'université joue un rôle crucial dans la reconstruction du tissu social. Nous intégrons des modules de résolution pacifique des conflits et d'éducation civique dans tous nos cursus.
                                </p>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4 mt-8">
                                {[
                                    { t: "Médiation", d: "Techniques de résolution de conflits." },
                                    { t: "Dialogue", d: "Espaces d'échange ouverts." },
                                    { t: "Civisme", d: "Formation à la citoyenneté." },
                                    { t: "Inclusion", d: "Respect de la diversité." }
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3 bg-secondary/5 border border-border/50 rounded-sm">
                                        <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                                        <div>
                                            <div className="text-[11px] font-bold uppercase tracking-tight">{item.t}</div>
                                            <div className="text-[10px] text-muted-foreground">{item.d}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Section 02 */}
                        <section id="actions">
                            <h2 className="heading-4 text-foreground mb-8 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Nos Actions Concrètes
                            </h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                {[
                                    { t: "Cours Transversaux", icon: BookOpen, d: "Modules obligatoires sur la culture de la paix et les droits de l'homme." },
                                    { t: "Clubs de Paix", icon: Users, d: "Associations étudiantes organisant débats, théâtres et actions." },
                                    { t: "Conférences", icon: Globe2, d: "Échanges avec des experts internationaux sur les enjeux régionaux." }
                                ].map((action, i) => (
                                    <div key={i} className="interactive-card p-6 border-l-4 border-primary transition-smooth">
                                        <action.icon className="w-6 h-6 text-primary mb-4" />
                                        <h3 className="heading-4 mb-2">{action.t}</h3>
                                        <p className="text-xs text-muted-foreground leading-relaxed">{action.d}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Section 03 */}
                        <section id="impact">
                            <div className="relative bg-primary text-white p-8 rounded-md overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full -mr-16 -mt-16" />
                                <p className="relative z-10 text-base italic font-serif leading-relaxed mb-6 font-light">
                                    "L'éducation est l'arme la plus puissante pour changer le monde."
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-accent border-2 border-white/20 flex items-center justify-center">
                                        <HeartHandshake className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-black uppercase tracking-wider">Nelson Mandela</div>
                                        <div className="text-[10px] text-white/60">Inspiration</div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </article>

            {/* CTA Footer */}
            <footer className="bg-primary text-white border-t-8 border-accent">
                <div className="container py-16 text-center max-w-2xl">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black mb-4 block">S'engager</span>
                    <h2 className="heading-2 mb-8 leading-tight">
                        Devenez un artisan<br />de la paix.
                    </h2>
                    {/* <Button className="bg-accent text-accent-foreground px-10 py-4 text-base font-black hover:bg-accent/90 transition-smooth group rounded-md shadow-2xl border-b-4 border-black/10">
                        Rejoindre le Mouvement
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button> */}
                </div>
            </footer>
        </div>
    )
}
